import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { fetchStockCategories, initializeDefaultStockCategories } from '../../utils/stockCategories';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../utils/roles';
import './Stock.css';

const StockItems = () => {
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isAdmin = userRole === USER_ROLES.ADMIN;

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Always fetch from database
        let fetchedCategories = [];
        try {
          fetchedCategories = await fetchStockCategories();
          // Initialize defaults if database is empty
          if (fetchedCategories.length === 0) {
            await initializeDefaultStockCategories();
            fetchedCategories = await fetchStockCategories();
          }
        } catch (catError) {
          console.error('Error fetching categories:', catError);
          // Try to initialize if fetch fails
          try {
            await initializeDefaultStockCategories();
            fetchedCategories = await fetchStockCategories();
          } catch (initError) {
            console.error('Error initializing categories:', initError);
          }
        }
        
        setCategories(fetchedCategories);
        await fetchStockItems();
      } catch (e) {
        console.error('Error loading data:', e);
        setError('Failed to load stock data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchStockItems = async () => {
    try {
      const itemsRef = collection(db, 'stock_items');
      const q = query(itemsRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      const itemsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
      }));
      setStockItems(itemsList);
      setError('');
    } catch (err) {
      console.error('Error fetching stock items:', err);
      setError('Failed to load stock items');
    }
  };

  const handleDelete = async (itemId) => {
    if (!isAdmin) {
      setError('Only administrators can delete stock items');
      return;
    }

    if (window.confirm('Are you sure you want to delete this stock item?')) {
      try {
        await deleteDoc(doc(db, 'stock_items', itemId));
        setStockItems(stockItems.filter(item => item.id !== itemId));
        setSuccess('Stock item deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error deleting stock item:', err);
        setError('Failed to delete stock item');
      }
    }
  };

  const handleToggleActive = async (item) => {
    if (!isAdmin) {
      setError('Only administrators can modify stock items');
      return;
    }

    try {
      await updateDoc(doc(db, 'stock_items', item.id), {
        isActive: !item.isActive
      });
      setStockItems(stockItems.map(i => 
        i.id === item.id ? { ...i, isActive: !i.isActive } : i
      ));
      setSuccess(`Stock item ${!item.isActive ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating stock item:', err);
      setError('Failed to update stock item');
    }
  };

  const getCategoryDetails = (categoryId) => {
    const found = categories.find(c => c.id === categoryId);
    return found || { id: categoryId, name: 'Other', color: '#95A5A6' };
  };

  const getFilteredItems = () => {
    let filtered = stockItems;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.categoryId === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
      );
    }

    // Only show active items by default (admins can see all)
    if (!isAdmin) {
      filtered = filtered.filter(item => item.isActive !== false);
    }

    return filtered;
  };

  const filteredItems = getFilteredItems();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
  };

  if (loading) {
    return <div className="loading">Loading stock items...</div>;
  }

  return (
    <div className="stock-items-container">
      <div className="stock-items-header">
        <h2>Stock Items</h2>
        <div className="header-actions">
          <div className="filters">
            <div className="category-filter">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="search-filter">
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          {isAdmin && (
            <button
              className="add-item-button"
              onClick={() => navigate('/stock/create')}
            >
              Add Stock Item
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="stock-items-summary">
        <div className="summary-item">
          <span className="summary-label">Total Items:</span>
          <span className="summary-value">{filteredItems.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Balance:</span>
          <span className="summary-value">
            {formatCurrency(filteredItems.reduce((sum, item) => sum + (item.currentBalance || 0), 0))}
          </span>
        </div>
      </div>

      <div className="stock-items-list">
        {filteredItems.length === 0 ? (
          <div className="no-items">
            No stock items found
            {selectedCategory !== 'all' && (
              <button
                className="clear-filter-button"
                onClick={() => setSelectedCategory('all')}
              >
                Clear Filter
              </button>
            )}
          </div>
        ) : (
          filteredItems.map(item => {
            const categoryDetails = getCategoryDetails(item.categoryId);
            const balance = item.currentBalance || 0;
            const isNegative = balance < 0;

            return (
              <div
                key={item.id}
                className={`stock-item-card ${isNegative ? 'low-stock' : ''} ${!item.isActive ? 'inactive' : ''}`}
              >
                <div
                  className="stock-item-icon"
                  style={{ backgroundColor: categoryDetails.color }}
                >
                  📦
                </div>
                <div className="stock-item-details">
                  <div className="stock-item-header">
                    <h3>{item.name}</h3>
                    {!item.isActive && (
                      <span className="inactive-badge">Inactive</span>
                    )}
                    {isNegative && (
                      <span className="low-stock-badge">Negative Balance</span>
                    )}
                  </div>
                  {item.description && (
                    <p className="stock-item-description">{item.description}</p>
                  )}
                  <div className="stock-item-meta">
                    <span className="stock-item-category" style={{ color: categoryDetails.color }}>
                      {categoryDetails.name}
                    </span>
                  </div>
                  <div className="stock-item-quantity">
                    <span className="quantity-label">Current Balance:</span>
                    <span className={`quantity-value ${isNegative ? 'low' : ''}`}>
                      {formatCurrency(balance)}
                    </span>
                  </div>
                </div>
                <div className="stock-item-actions">
                  {isAdmin && (
                    <>
                      <button
                        className="edit-button"
                        onClick={() => navigate(`/stock/create?id=${item.id}`)}
                      >
                        Edit
                      </button>
                      <button
                        className="toggle-active-button"
                        onClick={() => handleToggleActive(item)}
                      >
                        {item.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(item.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StockItems;

